import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer } from "recharts";
import { chartColors, chartFonts, sharedCartesianGridProps, sharedAxisProps, loadColor } from "./chartTheme";
import useReducedMotion from "./useReducedMotion";

function DarkTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;

  return (
    <div className="bg-rf-bg-secondary border border-rf-border rounded-lg px-3 py-2.5 shadow-lg">
      <div className="text-[11px] text-rf-text-muted font-mono mb-1.5">@{row.github_username}</div>
      <div className="flex flex-col gap-1 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-rf-text-secondary">Active reviews:</span>
          <span className="text-rf-text-primary font-mono font-medium">{row.active_reviews}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-rf-text-secondary">All-time assignments:</span>
          <span className="text-rf-text-primary font-mono font-medium">{row.assignments_all_time}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Horizontal bar chart of live reviewer workload. Bar color comes from the
 * shared utils/reviewLoad.js scale, so a reviewer reads the same here as in
 * the Dashboard's Team Capacity panel. (loadColor now returns a muted grey
 * for a non-number, so this relies on get-analytics running its rows through
 * int() first — COUNT is bigint, which node-postgres would otherwise hand
 * over as a string.)
 *
 * Deliberately a bar chart, not a distribution chart — a 2-10 person team
 * has nowhere near the point count a box/violin plot needs to be meaningful.
 */
function WorkloadBarChart({ data, ariaLabel }) {
  const reducedMotion = useReducedMotion();
  const height = Math.max(120, data.length * 44);

  return (
    <div>
      <div role="img" aria-label={ariaLabel} style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid {...sharedCartesianGridProps} horizontal={false} />
            <XAxis type="number" {...sharedAxisProps} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="github_username"
              {...sharedAxisProps}
              width={100}
              tickFormatter={(v) => `@${v}`}
            />
            <Tooltip content={<DarkTooltip />} cursor={{ fill: chartColors.bgTertiary }} />
            <Bar dataKey="active_reviews" radius={[0, 4, 4, 0]} isAnimationActive={!reducedMotion}>
              {data.map((row) => (
                <Cell key={row.user_id} fill={loadColor(row.active_reviews)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <details className="mt-2">
        <summary className="text-[11px] text-rf-text-muted cursor-pointer hover:text-rf-text-secondary">
          Show data table
        </summary>
        {/* Fixed height, matching TrendLineChart's data-table area exactly —
            Complexity Trend and Reviewer Workload sit side-by-side, so a
            mismatched height here is immediately visible. */}
        <div className="mt-2 h-[164px] overflow-y-auto overflow-x-auto">
          <table className="w-full text-xs" style={{ fontFamily: chartFonts.mono }}>
            <thead className="sticky top-0 bg-rf-bg-secondary">
              <tr className="text-rf-text-muted text-left">
                <th className="pr-4 py-1 font-normal">Reviewer</th>
                <th className="pr-4 py-1 font-normal">Active</th>
                <th className="pr-4 py-1 font-normal">All-time</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.user_id} className="text-rf-text-secondary">
                  <td className="pr-4 py-0.5">@{row.github_username}</td>
                  <td className="pr-4 py-0.5">{row.active_reviews}</td>
                  <td className="pr-4 py-0.5">{row.assignments_all_time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

export default WorkloadBarChart;
